import {
  ChevronDown,
  FileText,
  ImageOff,
  Info,
  Video,
  Loader2,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../utils/cn";
import Tooltip from "./Tooltip";

interface SelectOption {
  value: string;
  label: string;
  image?: string;
  isVideo?: boolean;
  // Explicitamente "esta opção representa um item com mídia, mas sem
  // imagem/vídeo disponível" — mostra um placeholder em vez de nada, para
  // diferenciar de opções que simplesmente não têm mídia associada (ex: uma
  // opção de texto livre).
  noMedia?: boolean;
  // Ícone mostrado dentro da mesma caixa 40x40 usada para thumbnails de
  // imagem/vídeo — mantém o alinhamento do texto consistente com as demais
  // opções que têm mídia (diferente de `icon`, que renderiza menor e inline,
  // sem essa caixa).
  boxIcon?: React.ReactNode;
  disabled?: boolean;
  icon?: React.ReactNode;
  count?: number;
}

interface SelectProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "onChange" | "size"
> {
  label?: string;
  labelTooltip?: string;
  error?: string;
  hint?: string;
  /** Mostra um asterisco vermelho ao lado do label, indicando campo obrigatório. */
  required?: boolean;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "filled" | "outline";
  isLoading?: boolean;
  /** Mostra o count da opção selecionada já no botão fechado, não só no dropdown. */
  showSelectedCount?: boolean;
  /** Classe aplicada só ao texto do botão fechado (ex.: reduzir a fonte sem afetar as opções do dropdown). */
  selectedLabelClassName?: string;
}

const ImageWithFallback: React.FC<{ src: string }> = ({ src }) => {
  const [error, setError] = useState(false);
  if (error) {
    return <ImageOff className="w-5 h-5 text-red-300" />;
  }
  return (
    <img
      src={src}
      alt=""
      className="w-full h-full object-cover"
      onError={() => setError(true)}
    />
  );
};

const Select: React.FC<SelectProps> = ({
  label,
  labelTooltip,
  error,
  hint,
  required,
  options,
  value,
  onChange,
  placeholder = "Selecione uma opção",
  size = "md",
  variant = "default",
  className,
  disabled,
  id,
  isLoading = false,
  showSelectedCount = false,
  selectedLabelClassName,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    openUp: false,
  });
  const selectRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const [isLabelTruncated, setIsLabelTruncated] = useState(false);

  const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
  const selectedOption = options.find((opt) => opt.value === value);

  // Only show a tooltip when the label is actually cut off — truncation
  // should be the exception (handled via layout width), not the default.
  useEffect(() => {
    const el = labelRef.current;
    if (el) setIsLabelTruncated(el.scrollWidth > el.clientWidth);
  }, [selectedOption?.label, placeholder, size]);

  const updateDropdownPosition = () => {
    if (selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect();
      // Estimativa da altura do menu (mesmo teto do max-h-60 = 240px do <ul>)
      const estimatedMenuHeight = Math.min(options.length * 36 + 8, 240);
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUp =
        spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;

      setDropdownPosition({
        top: openUp
          ? rect.top - Math.min(estimatedMenuHeight, spaceAbove) - 4
          : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        openUp,
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      const handleScroll = () => updateDropdownPosition();
      window.addEventListener("resize", handleScroll);
      window.addEventListener("scroll", handleScroll, true);
      return () => {
        window.removeEventListener("resize", handleScroll);
        window.removeEventListener("scroll", handleScroll, true);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideSelect = selectRef.current?.contains(target);
      const isInsideList = listRef.current?.contains(target);

      if (!isInsideSelect && !isInsideList) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && focusedIndex !== -1 && listRef.current) {
      const focusedItem = listRef.current.children[focusedIndex] as HTMLElement;
      if (focusedItem) {
        focusedItem.scrollIntoView({ block: "nearest" });
      }
    }
  }, [focusedIndex, isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        if (isOpen) {
          if (focusedIndex !== -1) {
            onChange(options[focusedIndex].value);
            setIsOpen(false);
          }
        } else {
          setIsOpen(true);
        }
        break;
      case "ArrowDown":
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setFocusedIndex((prev) =>
            prev < options.length - 1 ? prev + 1 : prev,
          );
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Escape":
        setIsOpen(false);
        break;
      case "Tab":
        setIsOpen(false);
        break;
    }
  };

  const handleSelect = (option: SelectOption) => {
    if (option.disabled) return;
    onChange(option.value);
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  return (
    <div className="space-y-1.5">
      {label && (
        <label
          htmlFor={selectId}
          className="flex items-center gap-1.5 text-xs sm:text-sm font-medium text-gray-700"
        >
          <span>
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </span>
          {labelTooltip && (
            <Tooltip
              content={labelTooltip}
              position="top"
              usePortal={true}
              delay={100}
            >
              <button
                type="button"
                className="text-gray-400 hover:text-purple-500 transition-colors p-0.5 rounded-full hover:bg-purple-50"
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          )}
          {hint && (
            <Tooltip content={hint}>
              <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 transition-colors cursor-help" />
            </Tooltip>
          )}
        </label>
      )}

      <div ref={selectRef} className="relative">
        <button
          type="button"
          id={selectId}
          disabled={disabled || isLoading}
          onClick={() => !disabled && !isLoading && setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          {...rest}
          className={cn(
            "w-full flex items-center justify-between transition-all duration-200",
            "rounded-xl text-left focus:outline-hidden focus:ring-2 focus:ring-purple-500/20",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            variant === "default" &&
              "border border-purple-300 bg-white hover:border-purple-400",
            variant === "filled" && "border-0 bg-gray-100 focus:bg-white",
            variant === "outline" &&
              "border-2 border-purple-300 bg-transparent",
            size === "sm" && "px-3.5 py-2 text-xs sm:text-sm h-10",
            size === "md" &&
              "px-4 py-2.5 sm:py-3 text-sm sm:text-sm md:text-base h-[42px] sm:h-[46px] md:h-[50px]",
            size === "lg" &&
              "px-5 py-2.5 sm:py-3 md:py-4 text-base sm:text-base md:text-lg h-[44px] sm:h-[50px] md:h-[56px]",
            isOpen && "border-purple-500 ring-2 ring-purple-500/20",
            error &&
              "border-red-300 focus:border-red-500 focus:ring-red-500/20",
            className,
          )}
        >
          <Tooltip
            content={selectedOption ? selectedOption.label : placeholder}
            position="top"
            disabled={!isLabelTruncated}
            wrap={false}
            wrapperClassName="min-w-0 flex-1"
          >
            <span
              ref={labelRef}
              className={cn(
                "flex items-center gap-1 truncate",
                !selectedOption && "text-gray-500",
                selectedLabelClassName,
              )}
            >
              {selectedOption?.icon && (
                <span className="shrink-0 [&>svg]:h-3.5 [&>svg]:w-3.5">
                  {selectedOption.icon}
                </span>
              )}
              {selectedOption ? selectedOption.label : placeholder}
            </span>
          </Tooltip>
          {showSelectedCount && selectedOption?.count !== undefined && (
            <span className="shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums bg-purple-100 text-purple-700 ml-1.5">
              {selectedOption.count}
            </span>
          )}
          {isLoading ? (
            <Loader2 className="ml-2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400 animate-spin shrink-0" />
          ) : (
            <ChevronDown
              className={cn(
                "ml-2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400 transition-transform duration-200",
                isOpen && "rotate-180 text-purple-500",
              )}
            />
          )}
        </button>

        {isOpen &&
          typeof document !== "undefined" &&
          createPortal(
            <div
              className={cn(
                "fixed z-100000 pointer-events-auto transition-all animate-in fade-in duration-200",
                dropdownPosition.openUp
                  ? "slide-in-from-bottom-2"
                  : "slide-in-from-top-2",
              )}
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${Math.max(dropdownPosition.width, 200)}px`,
              }}
            >
              <ul
                ref={listRef}
                className={cn(
                  "max-h-60 w-full overflow-y-auto overflow-x-hidden rounded-xl bg-white p-1 shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-hidden custom-scrollbar border border-purple-200 space-y-0.5",
                  size === "sm" && "text-xs",
                  size === "md" && "text-sm sm:text-sm md:text-base",
                  size === "lg" && "text-base sm:text-base md:text-lg",
                )}
                role="listbox"
              >
                {options.length === 0 ? (
                  <li className="px-4 py-2 text-gray-500 italic">
                    Nenhuma opção disponível
                  </li>
                ) : (
                  options.map((option, index) => (
                    <li
                      key={option.value}
                      className={cn(
                        "relative cursor-pointer select-none py-2 px-2.5 rounded-lg transition-colors",
                        option.value === value
                          ? "bg-purple-600 text-white font-bold shadow-xs shadow-purple-500/30"
                          : "text-gray-900",
                        focusedIndex === index &&
                          option.value !== value &&
                          "bg-purple-50",
                        option.value !== value && "hover:bg-purple-50",
                        "group",
                      )}
                      onContextMenu={(e) => {
                        if (!option.disabled) {
                          e.preventDefault();
                          handleSelect(option);
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect(option);
                      }}
                      onMouseEnter={() => setFocusedIndex(index)}
                      role="option"
                      aria-selected={option.value === value}
                      title={option.label}
                    >
                      <div className="flex items-center gap-1.5 min-w-0">
                        {(option.image ||
                          option.isVideo ||
                          option.noMedia ||
                          option.boxIcon) && (
                          <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-50 shrink-0 border border-purple-100 shadow-xs flex items-center justify-center relative group-hover:border-purple-300 transition-colors">
                            {option.image &&
                            !option.image.match(/\.(mp4|webm|mov|ogg)$/i) ? (
                              <>
                                <ImageWithFallback src={option.image} />
                                {option.isVideo && (
                                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                    <Video className="w-4 h-4 text-white drop-shadow-md" />
                                  </div>
                                )}
                              </>
                            ) : option.isVideo ? (
                              <div className="w-full h-full bg-gray-900 flex items-center justify-center">
                                <Video className="w-5 h-5 text-white" />
                              </div>
                            ) : option.noMedia ? (
                              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                <ImageOff className="w-4 h-4 text-gray-300" />
                              </div>
                            ) : option.boxIcon ? (
                              <div className="w-full h-full bg-purple-50 flex items-center justify-center [&>svg]:w-4 [&>svg]:h-4 [&>svg]:text-purple-500">
                                {option.boxIcon}
                              </div>
                            ) : (
                              <div className="w-full h-full bg-gray-900 flex items-center justify-center">
                                <FileText className="w-5 h-5 text-purple-200" />
                              </div>
                            )}
                          </div>
                        )}
                        {option.icon && !option.image && !option.isVideo && (
                          <span
                            className={cn(
                              "shrink-0 [&>svg]:h-4 [&>svg]:w-4",
                              option.value === value
                                ? "text-white"
                                : "text-gray-400 group-hover:text-purple-500",
                            )}
                          >
                            {option.icon}
                          </span>
                        )}
                        <span className="block truncate min-w-0 flex-1">
                          {option.label}
                        </span>
                        {option.count !== undefined && (
                          <span
                            className={cn(
                              "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums",
                              option.value === value
                                ? "bg-white/25 text-white"
                                : "bg-gray-100 text-gray-500 group-hover:bg-purple-100 group-hover:text-purple-700",
                            )}
                          >
                            {option.count}
                          </span>
                        )}
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </div>,
            document.body,
          )}
      </div>

      {error && (
        <p className="mt-1 text-xs sm:text-sm text-red-600 font-medium animate-in fade-in slide-in-from-top-1">
          {error}
        </p>
      )}
    </div>
  );
};

export default Select;
